import { addDays, addMonths, format, min, startOfDay } from 'date-fns';
import { z } from 'zod';

import { getErrorCode, isHttpStatus } from '@/lib/error-utils';
import { emailFormatError } from '@/lib/schemas';
import type {
  GiftCardCartItem,
  GiftCardReceiptCode,
  GiftCardReceiptSpend,
  GiftCardSpend,
  GiftCardSpotSpend,
  Order,
} from '@/lib/types';
import {
  formatAmount,
  formatDateInTimeZone,
  formatDateWithConditionalYear,
  formatInTimeZone,
  formatPrice,
} from '@/lib/utils';
import type {
  CreateGiftCardOrderPayload,
  GiftCardRecipient,
} from '@/services/order';
import type { AcceptedTerms } from '@/services/terms';
import type { MetaAttribution } from '@/services/tracking';
import type {
  GiftCardCodeLookup,
  GiftCardRedemption,
} from '@/state/gift-card-codes';
import type { GiftCard } from '@/state/gift-cards';

const countOf = (count: number, singular: string, plural: string) =>
  `${count} ${count === 1 ? singular : plural}`;

// Credit days count from redemption, and the credits never end before the
// card does (D220): the later of the two, named plainly rather than as "at
// least" the days.
const creditValidity = (days: number) =>
  `until the card ends, or ${countOf(days, 'day', 'days')} after you redeem if that's later`;

// What the value lines read: a catalog card and a sold card's receipt both
// carry it.
type GiftCardValueSource =
  | {
      kind: 'CLASSES';
      number_of_classes: number;
      credit_expiry_days: number | null;
      // A catalog card's redeem-within. null: the card never expires, so its
      // credits never do either. A sold card's builders fold its date into
      // credit_expiry_days instead.
      validity_months?: number | null;
    }
  | {
      kind: 'MONEY';
      price: number;
      currency: string;
      // What it pays for (gift cards M1): a catalog card's lists. Left out
      // reads as classes.
      class_ids?: string[];
      class_pack_ids?: string[];
      subscription_ids?: string[];
    };

// What a money card is spent on, the groups it lists in this order (gift
// cards M1, D309): a hand-copy of the backend's giftCardSpendsOn (the gift
// email) and the admin preview's. Change all three in the same piece of work.
// Nothing listed reads as classes, the card's first group.
export const giftCardSpendsOn = ({
  class_ids,
  class_pack_ids,
  subscription_ids,
}: {
  class_ids?: string[];
  class_pack_ids?: string[];
  subscription_ids?: string[];
}): string => {
  const groups = [
    class_ids?.length ? 'classes' : null,
    class_pack_ids?.length ? 'class packs' : null,
    subscription_ids?.length ? 'memberships' : null,
  ].filter((group): group is string => group !== null);
  if (!groups.length) return 'classes';
  return groups.length === 1
    ? groups[0]
    : `${groups.slice(0, -1).join(', ')} and ${groups[groups.length - 1]}`;
};

// null: the credits have no time limit.
const creditDaysOf = (
  source: Extract<GiftCardValueSource, { kind: 'CLASSES' }>
): number | null =>
  source.validity_months === null ? null : source.credit_expiry_days;

// A sold card with no date gives credits that never expire, whatever its
// credit days (D220).
const soldCreditDays = (code: {
  credit_expiry_days: number | null;
  expires_at: string | null;
}): number | null =>
  code.expires_at === null ? null : code.credit_expiry_days;

// The card's headline. A MONEY card's price is its amount.
export const giftCardValue = (giftCard: GiftCardValueSource): string =>
  giftCard.kind === 'MONEY'
    ? formatPrice(giftCard.price, giftCard.currency)
    : countOf(giftCard.number_of_classes, 'class', 'classes');

export const giftCardValueLine = (giftCard: GiftCardValueSource): string => {
  if (giftCard.kind === 'MONEY') {
    return `${giftCardValue(giftCard)} to spend on ${giftCardSpendsOn(giftCard)}`;
  }
  const days = creditDaysOf(giftCard);
  return days == null
    ? giftCardValue(giftCard)
    : `${giftCardValue(giftCard)}, valid ${creditValidity(days)}`;
};

export const receiptGiftCardValue = (
  code: GiftCardReceiptCode
): GiftCardValueSource =>
  code.kind === 'MONEY'
    ? { kind: 'MONEY', price: code.amount, currency: code.currency }
    : {
        kind: 'CLASSES',
        number_of_classes: code.number_of_classes,
        credit_expiry_days: soldCreditDays(code),
      };

// Price first. A MONEY card's value line already opens with it, since its
// price is its amount. The credit validity has its own line
// (giftCardCreditLine): beside the price it wraps at 375 px and leaves a
// stranded dot.
export const giftCardMeta = (giftCard: GiftCard): string[] =>
  giftCard.kind === 'MONEY'
    ? [giftCardValueLine(giftCard)]
    : [formatPrice(giftCard.price, giftCard.currency), giftCardValue(giftCard)];

export const giftCardCreditLine = (giftCard: GiftCard): string | null => {
  if (giftCard.kind === 'MONEY') return null;
  const days = creditDaysOf(giftCard);
  return days == null ? null : `Classes last ${creditValidity(days)}`;
};

// The deadline runs from the day the gift card is sent, which is the day it
// is bought unless the buyer picks a later day (D219): for a CLASSES card it
// is the last day to redeem, for a MONEY card the day the balance expires.
// Where the code runs on to 31 December, the months alone would state a
// deadline up to a year shorter than the card gives (D251).
export const giftCardRedeemWithin = ({
  kind,
  validity_months,
  validity_to_year_end,
}: Pick<
  GiftCard,
  'kind' | 'validity_months' | 'validity_to_year_end'
>): string => {
  const isMoney = kind === 'MONEY';
  if (validity_months == null) {
    return isMoney ? 'No deadline to spend it' : 'No deadline to redeem';
  }
  const within = `within ${countOf(validity_months, 'month', 'months')} from the day it is sent${validity_to_year_end ? ', until the end of that year' : ''}`;
  return isMoney ? `Spend it ${within}` : `Redeem ${within}`;
};

// The list endpoint answers ACTIVE and PUBLIC gift cards only.
export const hasPublicGiftCards = (giftCards: GiftCard[] | undefined) =>
  !!giftCards?.length;

// A backend without gift cards has no list route and answers 404: that studio
// sells none, so no tab, group card or Buy entry leads to a dead page, and the
// sitemap still lists everything else. Any other failure stays one: the tab
// fails open on it, and the sitemap refuses to answer.
export const noGiftCardsOnNotFound = (
  error: unknown
): { gift_cards: GiftCard[] } => {
  if (isHttpStatus(error, 404)) return { gift_cards: [] };
  throw error;
};

// One gift card per order: the create carries no quantity and charges for
// one, so a cart handed over with more would quote a total nobody pays.
export const giftCartItems = <T extends { quantity: number }>(
  items: T[]
): T[] => items.map((item) => ({ ...item, quantity: 1 }));

// The create's plain 404 (gift sales closed, D319, or a plan without gift
// cards, D316, or the card taken off sale) and GIFT_CARD_NO_CLASSES: pressing
// again cannot change either.
export const GIFT_CARD_UNAVAILABLE_COPY =
  "This gift card can't be bought right now.";

export const GIFT_RECIPIENT_NAME_MAX_LENGTH = 100;
export const GIFT_MESSAGE_MAX_LENGTH = 200;
const SEND_AHEAD_DAYS = 90;
const SEND_DAYS_BEFORE_EXPIRY = 30;

// The studio's wall clock at an instant, to the minute. sv-SE formats as
// "2026-10-13 09:05" whatever the browser's own locale.
const studioWallFields = (
  now: Date,
  timeZone: string | undefined
): [number, number, number, number, number] => {
  const [year, month, day, hour, minute] = formatInTimeZone(
    now,
    timeZone,
    {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    },
    'sv-SE'
  )
    .split(/\D+/)
    .map(Number);
  return [year, month, day, hour, minute];
};

// The studio's wall clock as a browser-local Date with the same fields, so the
// calendar arithmetic below runs on the studio's days, as the server's does.
const studioWallClock = (now: Date, timeZone: string | undefined): Date => {
  const [year, month, day, hour, minute] = studioWallFields(now, timeZone);
  return new Date(year, month - 1, day, hour, minute);
};

// The last moment of a studio day, where the server ends gift credits (D219,
// D220). `day` is a browser-local midnight standing for that studio day. A
// second pass lands on the right offset when the clocks change in between.
const studioDayEnd = (day: Date, timeZone: string | undefined): Date => {
  const wanted = Date.UTC(
    day.getFullYear(),
    day.getMonth(),
    day.getDate(),
    23,
    59,
    59,
    999
  );
  let instant = wanted;
  for (let pass = 0; pass < 2; pass += 1) {
    const [year, month, date, hour, minute] = studioWallFields(
      new Date(instant),
      timeZone
    );
    instant += wanted - Date.UTC(year, month - 1, date, hour, minute, 59, 999);
  }
  return new Date(instant);
};

export type SendDateBounds = { from: Date; to: Date };

// The days a gift may be sent on: the studio's today up to 90 days on, and with
// a finite redeem-within no day whose 09:00 send is less than 30 days before
// the server's estimate of the expiry from now (giftCardExpiresAt). The code a
// sale mints counts from the send day and never ends earlier (D219). That
// estimate is the end of the studio's day N months on, so the 09:00 of the day
// 30 days before it always fits, whatever the hour now. The server checks the
// same rule at create (sendDateRefusal). Null when no day qualifies, so the
// gift can only go out now.
export const giftCardSendDateBounds = (
  now: Date,
  validityMonths: number | null,
  timeZone: string | undefined
): SendDateBounds | null => {
  const wallClock = studioWallClock(now, timeZone);
  const from = startOfDay(wallClock);
  const furthest = addDays(from, SEND_AHEAD_DAYS);
  const to =
    validityMonths == null
      ? furthest
      : min([
          furthest,
          startOfDay(
            addDays(
              addMonths(wallClock, validityMonths),
              -SEND_DAYS_BEFORE_EXPIRY
            )
          ),
        ]);
  return to < from ? null : { from, to };
};

export type GiftRecipientValues = {
  name: string;
  email: string;
  sendToSelf: boolean;
  message: string;
  sendWhen: 'now' | 'date';
  // A browser-local midnight standing for a day in the studio's calendar.
  sendOn: Date | null;
};

export const GIFT_RECIPIENT_DEFAULTS: GiftRecipientValues = {
  name: '',
  email: '',
  sendToSelf: false,
  message: '',
  sendWhen: 'now',
  sendOn: null,
};

const formatSendDay = (day: Date) => formatDateInTimeZone(day, undefined);

// A send-to-me gift always goes out at payment: the checkout hides the day
// then, and keeps whatever was picked only for turning send-to-me off again.
const sendsOnDay = (values: GiftRecipientValues) =>
  !values.sendToSelf && values.sendWhen === 'date';

const recipientEmailError = (email: string): string | null =>
  email ? emailFormatError(email) : "Enter the recipient's email";

// The backend's PLAIN_TEXT: control characters other than tab and line breaks
// are refused, and a refused body is an ops alarm and a generic error to the
// buyer, so the card checks first. A paste from a chat or a document can carry
// one nobody sees (D252).
const GIFT_PLAIN_TEXT = /^[^\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]*$/;

export const giftRecipientSchema = (bounds: SendDateBounds | null) =>
  z
    .object({
      name: z
        .string()
        .trim()
        .min(1, "Enter the recipient's name")
        .max(
          GIFT_RECIPIENT_NAME_MAX_LENGTH,
          `Keep the name to ${GIFT_RECIPIENT_NAME_MAX_LENGTH} characters`
        )
        .regex(
          GIFT_PLAIN_TEXT,
          'The name has hidden characters, often from a paste. Type it here instead.'
        ),
      email: z.string(),
      sendToSelf: z.boolean(),
      message: z
        .string()
        .trim()
        .max(
          GIFT_MESSAGE_MAX_LENGTH,
          `Keep the message to ${GIFT_MESSAGE_MAX_LENGTH} characters`
        )
        .regex(
          GIFT_PLAIN_TEXT,
          'The message has hidden characters, often from a paste. Type it here instead.'
        ),
      sendWhen: z.enum(['now', 'date']),
      sendOn: z.date().nullable(),
    })
    .superRefine((values, ctx) => {
      if (!values.sendToSelf) {
        const message = recipientEmailError(values.email.trim().toLowerCase());
        if (message) ctx.addIssue({ code: 'custom', path: ['email'], message });
      }
      if (!sendsOnDay(values)) return;
      const day = values.sendOn;
      if (!day || !bounds) {
        ctx.addIssue({
          code: 'custom',
          path: ['sendOn'],
          message: 'Pick a date',
        });
      } else if (day < bounds.from || day > bounds.to) {
        ctx.addIssue({
          code: 'custom',
          path: ['sendOn'],
          message: `Pick a date from ${formatSendDay(bounds.from)} to ${formatSendDay(bounds.to)}`,
        });
      }
    });

export const toGiftCardRecipient = (
  values: GiftRecipientValues
): GiftCardRecipient => ({
  name: values.name.trim(),
  ...(!values.sendToSelf && { email: values.email.trim().toLowerCase() }),
  message: values.message.trim() || null,
  send_to_self: values.sendToSelf,
  send_on:
    sendsOnDay(values) && values.sendOn
      ? format(values.sendOn, 'yyyy-MM-dd')
      : null,
});

// Every key named here: the route refuses a body carrying any coupon or
// discount key, even an empty one.
export const buildGiftCardOrderBody = ({
  item,
  recipient,
  customer,
  acceptedTerms,
  metaAttribution,
}: {
  item: Pick<GiftCardCartItem, 'gift_card_id' | 'price'>;
  recipient: GiftRecipientValues;
  customer: Pick<
    CreateGiftCardOrderPayload,
    'user_id' | 'first_name' | 'last_name' | 'email' | 'phone' | 'country_code'
  >;
  acceptedTerms: { accepted_terms?: AcceptedTerms[] };
  metaAttribution: MetaAttribution;
}): CreateGiftCardOrderPayload => ({
  gift_card_id: item.gift_card_id,
  original_price: item.price,
  recipient: toGiftCardRecipient(recipient),
  user_id: customer.user_id,
  first_name: customer.first_name,
  last_name: customer.last_name,
  email: customer.email,
  phone: customer.phone,
  country_code: customer.country_code,
  accepted_terms: acceptedTerms.accepted_terms,
  meta_attribution: metaAttribution,
});

// The receipt carries the send instant, not the chosen day: a future one is a
// chosen day, a past one went out at payment or goes out now.
export const giftReceiptDeliveryLine = (
  code: GiftCardReceiptCode,
  timeZone: string | undefined,
  now: Date
): string => {
  const name = code.recipient_name;
  const forWhom = name ? ` for ${name}` : '';
  if (code.delivered_at) {
    const on = formatDateInTimeZone(new Date(code.delivered_at), timeZone);
    return code.send_to_self
      ? `We emailed your gift card${forWhom} to you on ${on}.`
      : `We emailed the gift card to ${name ?? 'the recipient'} on ${on}.`;
  }
  const deliverAt = new Date(code.deliver_at);
  const when =
    deliverAt > now
      ? `on ${formatDateInTimeZone(deliverAt, timeZone)}`
      : 'right away';
  return code.send_to_self
    ? `We email your gift card${forWhom} to you ${when}.`
    : `We email the gift card to ${name ?? 'the recipient'} ${when}.`;
};

// A card that is no longer ACTIVE is never emailed (D103) and its code no
// longer works, so the receipt says what became of it instead of promising an
// email or offering the code. Null for an ACTIVE card.
export const giftReceiptStatusLine = (
  code: Pick<GiftCardReceiptCode, 'status'>
): string | null => {
  switch (code.status) {
    case 'ACTIVE':
      return null;
    case 'REDEEMED':
      return 'This gift card has been redeemed.';
    case 'USED_UP':
      return 'Nothing is left on this gift card.';
    case 'EXPIRED':
      return 'This gift card has expired.';
    case 'DEACTIVATED':
      return 'This gift card no longer works.';
  }
};

// The card's own date, as the server minted it. End dates read in the
// visitor's day, as every pack and plan date on the site does.
export const giftReceiptValidUntil = (
  code: Pick<GiftCardReceiptCode, 'expires_at'>
): string =>
  code.expires_at
    ? formatDateWithConditionalYear(code.expires_at)
    : 'No expiry date';

export const giftReceiptCreditValidity = (
  code: Pick<GiftCardReceiptCode, 'kind' | 'credit_expiry_days' | 'expires_at'>
): string | null => {
  const days = code.kind === 'CLASSES' ? soldCreditDays(code) : null;
  return days == null
    ? null
    : `Until the card ends, or ${countOf(days, 'day', 'days')} after you redeem if that's later`;
};

// The day credits redeemed today would end, so the gift page can name it
// rather than say "at least": the server's rule (giftCreditExpiryDays, D220)
// is the card's credit days from the studio's today, but never ending before
// the card's own day. The rule runs on studio days; the day it names is the
// visitor's, as the redeemed pack's own end date reads after. cardDay says
// when that day is the card's own, so the page can name it once. Null when the
// credits never expire.
export const giftCreditsEndIfRedeemedToday = (
  code: Pick<GiftCardReceiptCode, 'kind' | 'credit_expiry_days' | 'expires_at'>,
  now: Date,
  timeZone: string | undefined
): { day: string; cardDay: boolean } | null => {
  const days = code.kind === 'CLASSES' ? soldCreditDays(code) : null;
  if (days == null || !code.expires_at) return null;
  const creditsEnd = addDays(startOfDay(studioWallClock(now, timeZone)), days);
  const cardEnd = startOfDay(
    studioWallClock(new Date(code.expires_at), timeZone)
  );
  return creditsEnd > cardEnd
    ? {
        day: formatDateWithConditionalYear(studioDayEnd(creditsEnd, timeZone)),
        cardDay: false,
      }
    : { day: formatDateWithConditionalYear(code.expires_at), cardDay: true };
};

export type GiftCodeClosedState =
  | Exclude<GiftCardCodeLookup['state'], 'ACTIVE'>
  | 'NOT_FOUND';

type ClassesGiftCodeLookup = Extract<GiftCardCodeLookup, { kind: 'CLASSES' }>;
type MoneyGiftCodeLookup = Extract<GiftCardCodeLookup, { kind: 'MONEY' }>;

// What the recipient page renders for a lookup: a classes card to redeem, a
// money card to spend at checkout, or a state that says why it can't be used.
// A null lookup is a code this studio does not know.
export type GiftCodeView =
  | { kind: 'redeem'; card: ClassesGiftCodeLookup }
  | { kind: 'spend'; card: MoneyGiftCodeLookup }
  | { kind: 'closed'; state: GiftCodeClosedState };

export const giftCodeView = (
  lookup: GiftCardCodeLookup | null
): GiftCodeView => {
  if (!lookup) return { kind: 'closed', state: 'NOT_FOUND' };
  if (lookup.state !== 'ACTIVE') return { kind: 'closed', state: lookup.state };
  return lookup.kind === 'CLASSES'
    ? { kind: 'redeem', card: lookup }
    : { kind: 'spend', card: lookup };
};

// Each says why the card can't be used and who can help, without blame. A
// buyer's chargeback shuts a card as DEACTIVATED too (D230, D247), and its
// lookup is the same as that of a card the studio stopped, so that sentence
// names no one.
export const GIFT_CODE_STATE_COPY: Record<
  GiftCodeClosedState,
  { title: string; description: string }
> = {
  NOT_FOUND: {
    title: "We can't find this gift card",
    description:
      'Check that you opened the link from your gift email. If it still does not work, the studio can help.',
  },
  REDEEMED: {
    title: 'This gift card is already redeemed',
    description:
      'Its classes are on the account that redeemed it. Log in with that account to book them.',
  },
  USED_UP: {
    title: 'This gift card is used up',
    description: 'All the money on it has been spent.',
  },
  EXPIRED: {
    title: 'This gift card has expired',
    description:
      'The time to use it has run out. Please contact the studio. They may be able to help.',
  },
  DEACTIVATED: {
    title: 'This gift card no longer works',
    description:
      'It can no longer be used. Please contact the studio if you have any questions.',
  },
};

// The face value: a money card's amount as sold, never its balance.
export const lookupGiftCardValue = (
  lookup: GiftCardCodeLookup
): GiftCardValueSource =>
  lookup.kind === 'MONEY'
    ? { kind: 'MONEY', price: lookup.initial_amount, currency: lookup.currency }
    : {
        kind: 'CLASSES',
        number_of_classes: lookup.number_of_classes,
        credit_expiry_days: soldCreditDays(lookup),
      };

// The gift email's own heading, so the page reads like the email it opened.
export const giftCodeHeading = (senderFirstName: string | null): string =>
  senderFirstName ? `${senderFirstName} sent you a gift` : "You've got a gift";

// The email says "Expires on", for both kinds; a card that never expires has no
// such day.
export const giftCodeDeadline = (
  lookup: GiftCardCodeLookup
): { title: string; label: string } => {
  if (!lookup.expires_at)
    return { title: 'No expiry date', label: 'Valid until' };
  return {
    title: formatDateWithConditionalYear(lookup.expires_at),
    label: 'Expires on',
  };
};

export const giftRedeemedValue = (redemption: GiftCardRedemption): string =>
  `${countOf(redemption.number_of_classes, 'class is', 'classes are')} on your account`;

// The same day the receipt of a booking made with these credits shows.
export const giftCreditsUntil = (redemption: GiftCardRedemption): string =>
  redemption.credits_expire_at
    ? `Use them by ${formatDateWithConditionalYear(redemption.credits_expire_at)}.`
    : 'They never expire.';

// The backend's own strip. `\s` misses the invisible format characters (a
// zero-width space, a bidi mark) a pasted code can carry, hence `\p{Cf}`, and
// a chat app rewrites hyphens as en dashes, hence `\p{Pd}` over a literal `-`,
// plus the minus sign, which is not dash punctuation.
const normalizeGiftCode = (raw: string): string =>
  raw.toUpperCase().replace(/[\s\p{Cf}\p{Pd}−]/gu, '');

// The canonical alphabet (no 0/O, no 1/I), so a label such as "GFTCODE" in
// front of a code cannot start a wrong run.
const GIFT_CODE_RUN = /GFT[A-HJ-NP-Z2-9]{12}/;

// A code anywhere in the field, as pasted from a chat line, a quote or the
// gift email's value line, formatted the way the backend prints it.
export const extractGiftCardCode = (raw: string): string | null => {
  const run = normalizeGiftCode(raw).match(GIFT_CODE_RUN)?.[0];
  return run ? formatGiftCode(run) : null;
};

// A whole email address by the test the coupon payload sends it under
// (`isEmailAsCoupon` in the checkout page's handleVerifyCoupon), the credit
// check's own code.
// Only the whole field: a code pasted beside an address is still a code.
const isWholeEmail = (raw: string): boolean =>
  z.string().email().safeParse(raw.trim()).success;

// Deliberately a prefix test, not the backend's exact shape: a code typed a
// character off still names most of a real one, and the discount check
// carries its code in a query string and its analytics events. D13 keeps the
// prefix for gift cards, and a code anywhere in the field is one too (D158).
// An email is the credit check's code, whatever run its letters happen to
// hold, and a pack or membership code is 8 characters, so neither is ever
// read as one.
export const looksLikeGiftCardCode = (raw: string): boolean => {
  if (isWholeEmail(raw)) return false;
  const normalized = normalizeGiftCode(raw);
  return (
    (normalized.length > 8 && normalized.startsWith('GFT')) ||
    GIFT_CODE_RUN.test(normalized)
  );
};

// The backend's log mask: GFT and 12 letters or digits however they were
// typed, wherever they sit. A backstop on what a discount check reports to
// analytics, for a near miss the classifier above let through.
const LOOSE_GIFT_CODE =
  /G[\s\p{Cf}\p{Pd}−]*F[\s\p{Cf}\p{Pd}−]*T(?:[\s\p{Cf}\p{Pd}−]*[A-Z0-9]){12}/giu;

export const maskGiftCodeText = (text: string): string =>
  text.replace(LOOSE_GIFT_CODE, 'GFT-[REDACTED]');

// The backend's own display form, GFT-XXXX-XXXX-XXXX, from the canonical code
// the email links to.
export const formatGiftCode = (code: string): string => {
  const canonical = normalizeGiftCode(code);
  const body = canonical.slice(3);
  return [
    canonical.slice(0, 3),
    body.slice(0, 4),
    body.slice(4, 8),
    body.slice(8),
  ]
    .filter(Boolean)
    .join('-');
};

// useParams hands the page its segment still percent-encoded, so a code typed
// with spaces or pasted with en dashes would reach the lookup as %20 and
// %E2%80%93, which the backend's strip does not read (D158). Next answers a
// malformed escape with a 400 before the page runs, so the catch is only a
// guard against the page throwing. The lookup caps the decoded code, not the
// path: escapes make a path longer than the code it carries.
export const giftCodeFromPath = (segment: string): string => {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
};

// The backend's GIFT_CARD_CODE_INPUT_MAX_LENGTH. A longer field fails its
// validation with a 422, which both apps report as a client bug, though here
// it is only text a person pasted (D204). It cannot hold a code the server
// would find, so it is answered as one it cannot, without asking.
const GIFT_CODE_FIELD_MAX_LENGTH = 64;

export const isGiftCodeFieldTooLong = (code: string): boolean =>
  code.trim().length > GIFT_CODE_FIELD_MAX_LENGTH;

// The redeem's coded refusals. One the lookup can also answer moves the page
// to that state, which says it in full; the rest are said under the button.
// Null for anything else: the caller's terms and sign-in handling, then the
// generic error.
export const giftRedeemRefusal = (
  code: string | undefined
): { state: GiftCodeClosedState } | { message: string } | null => {
  switch (code) {
    case 'GIFT_CARD_CODE_NOT_FOUND':
      return { state: 'NOT_FOUND' };
    case 'GIFT_CARD_ALREADY_REDEEMED':
      return { state: 'REDEEMED' };
    case 'GIFT_CARD_EXPIRED':
      return { state: 'EXPIRED' };
    case 'GIFT_CARD_DEACTIVATED':
      return { state: 'DEACTIVATED' };
    case 'GIFT_CARD_USE_AT_CHECKOUT':
      return {
        message: 'This gift card is used at checkout. Type the code there.',
      };
    case 'GIFT_CARD_NO_CLASSES':
      return {
        message:
          "None of this gift card's classes can be booked right now, so it can't be redeemed yet. Please contact the studio.",
      };
    default:
      return null;
  }
};

// The server's figures for a money card's share, printed as given and never
// worked out here (D20).
export const giftSpendDisplay = (spend: GiftCardSpend, currency: string) => ({
  applied: `−${formatAmount(spend.applied, currency)}`,
  remainder: formatAmount(spend.remainder, currency),
  balanceLeft:
    spend.balance_after > 0
      ? `${formatAmount(spend.balance_after, currency)} left on your gift card`
      : 'Nothing left on your gift card',
});

// The balance is the card's right after the order took its share (D124). A
// full dashboard refund puts the share back on the card and leaves the order
// REFUNDED (D307, D314), so from then on the balance is no longer the card's. On
// a pack or a first subscription payment that is the only way back. A class
// order is refunded the same way, and its receipt also checks cancelled spots.
export const wholeOrderGiftBalanceLine = (
  lines: { balanceLeft: string },
  paymentStatus: Order['payment_status']
): string | null => (paymentStatus === 'REFUNDED' ? null : lines.balanceLeft);

// An older backend sends no refunds_to_card. Read as true, the copy stays what
// it was before the flag existed.
export const refundsToGiftCard = (
  spend: GiftCardReceiptSpend | null | undefined
): boolean => spend?.refunds_to_card !== false;

type GiftSpot = { gift_card?: GiftCardSpotSpend | null };

const sumSpots = (spots: GiftSpot[], key: keyof GiftCardSpotSpend): number =>
  spots.reduce((sum, spot) => sum + (spot.gift_card?.[key] ?? 0), 0);

// A class receipt shows one class time, and a reschedule can spread an order
// over several, so its gift card figures are that class time's spots added
// up: the share and the rest of the priced spots (those still booked, or the
// cancelled ones once none is), and what went back to the card for any of its
// cancelled spots. The balance is the card's, whichever class time.
export const classTimeGiftSpend = (
  spend: GiftCardSpend,
  pricedSpots: GiftSpot[],
  slotSpots: GiftSpot[]
) => ({
  applied: sumSpots(pricedSpots, 'applied'),
  remainder: sumSpots(pricedSpots, 'remainder'),
  balance_after: spend.balance_after,
  refunded: sumSpots(slotSpots, 'refunded'),
});

// A top-up or another gift card, which a card never pays for (gift cards
// D6, D302); the backend's GIFT_CARD_NOT_ACCEPTED.
export const GIFT_CARD_NOT_ACCEPTED_COPY =
  "A gift card can't pay for top-ups or other gift cards.";

// What the checkout is buying, for the refusals worded per product (M1).
export type GiftSpendProduct = 'class' | 'class-pack' | 'subscription';

const PRODUCT_NOUN: Record<GiftSpendProduct, string> = {
  class: 'class',
  'class-pack': 'class pack',
  subscription: 'membership',
};

// What a money card is good for on its gift page, in one list: its classes,
// then its class packs, then its memberships (gift cards M1).
export const giftCardUsableNames = (card: {
  class_names: string[];
  class_pack_names?: string[];
  subscription_names?: string[];
}): string[] => [
  ...card.class_names,
  ...(card.class_pack_names ?? []),
  ...(card.subscription_names ?? []),
];

// Where the money card's button sends the holder: its classes first, else its
// packs, else its plans (M1). The caption keeps D301's words where it books.
export const giftSpendAction = (card: {
  class_names: string[];
  class_pack_names?: string[];
  subscription_names?: string[];
}): { label: string; path: string; caption: string } => {
  if (!card.class_names.length && card.class_pack_names?.length) {
    return {
      label: 'See class packs',
      path: '/packages',
      caption: 'Use the gift card code at checkout',
    };
  }
  if (!card.class_names.length && card.subscription_names?.length) {
    return {
      label: 'See memberships',
      path: '/subscriptions',
      caption: 'Use the gift card code at checkout',
    };
  }
  return {
    label: 'Book a class',
    path: '/classes',
    caption: 'Use the gift card code at checkout when you book',
  };
};

// The checkouts a money card pays for (M1); null for a top-up or a gift card
// purchase.
export const giftSpendProductOf = (
  checkoutType: string
): GiftSpendProduct | null =>
  checkoutType === 'class' ||
  checkoutType === 'class-pack' ||
  checkoutType === 'subscription'
    ? checkoutType
    : null;

// The cart a quote prices, as the create will: never a price, which the
// server reads from the catalog.
export type GiftSpendCart =
  | { purchase_type: 'CLASS'; class_id: string; quantity: number }
  | {
      purchase_type: 'CLASS_PACK';
      class_pack_items: { class_pack_id: string; quantity: number }[];
    }
  | { purchase_type: 'SUBSCRIPTION'; subscription_id: string };

type GiftSpendCartLine = {
  quantity: number;
  class_id?: string;
  class_pack_id?: string;
  subscription_id?: string;
};

// The checkout's own cart as the quote takes it. `quantity` is what the
// stepper just set, which the cart state has not caught up with yet: the
// class's spots, or the quantity of a pack cart's one line.
export const giftSpendCart = (
  product: GiftSpendProduct,
  cartItems: readonly GiftSpendCartLine[],
  quantity: number
): GiftSpendCart => {
  if (product === 'class') {
    return {
      purchase_type: 'CLASS',
      class_id: cartItems[0]?.class_id ?? '',
      quantity,
    };
  }
  if (product === 'class-pack') {
    return {
      purchase_type: 'CLASS_PACK',
      class_pack_items: cartItems.map((item) => ({
        class_pack_id: item.class_pack_id ?? '',
        quantity: cartItems.length === 1 ? quantity : item.quantity,
      })),
    };
  }
  return {
    purchase_type: 'SUBSCRIPTION',
    subscription_id: cartItems[0]?.subscription_id ?? '',
  };
};

export const giftCardNotCoveredCopy = (product: GiftSpendProduct = 'class') =>
  `This gift card can't be used for this ${PRODUCT_NOUN[product]}.`;

export const GIFT_CARD_NOT_COVERED_COPY = giftCardNotCoveredCopy('class');

export const GIFT_CARD_NOT_FOUND_COPY =
  "We couldn't find that gift card. Check the code and try again.";

const giftSpendRefusalCopy = (
  product: GiftSpendProduct
): Record<string, string> => {
  const noun = PRODUCT_NOUN[product];
  return {
    GIFT_CARD_CODE_NOT_FOUND: GIFT_CARD_NOT_FOUND_COPY,
    GIFT_CARD_REDEEM_ON_GIFT_PAGE:
      'This gift card is for classes. Redeem it on your gift card page first, then book with your credits.',
    GIFT_CARD_DEACTIVATED:
      'This gift card is no longer valid. Please contact the studio.',
    GIFT_CARD_EXPIRED: 'This gift card has expired.',
    GIFT_CARD_USED_UP: 'This gift card has no balance left.',
    GIFT_CARD_BALANCE_IN_USE:
      'This gift card is being used in another checkout. Finish or close that checkout, then try again.',
    GIFT_CARD_CURRENCY_MISMATCH: `This gift card is in a different currency, so it can't pay for this ${noun}.`,
    GIFT_CARD_CLASS_NOT_COVERED: giftCardNotCoveredCopy(product),
    GIFT_CARD_PRODUCT_NOT_COVERED: giftCardNotCoveredCopy(product),
    GIFT_CARD_BELOW_MINIMUM_CHARGE: `This gift card can't pay part of this ${product === 'class' ? 'booking' : 'purchase'}, because the amount left to pay would be too small.`,
    GIFT_CARD_NOTHING_TO_PAY: `This ${noun} is free, so there is nothing to pay with a gift card.`,
    GIFT_CARD_PLAN_SWITCH: "A gift card can't pay for a plan switch.",
    CURRENCY_FLOOR_UNKNOWN: "Gift cards can't be used at this studio right now.",
  };
};

// Every card state validate and the create refuse is worded here, per the
// product the checkout buys (M1), and so is the create's not-covered refusal
// (validate answers that one as `covers` false). A code missing here is not
// about the card, or it is a client bug (a discount or a gift code in the
// wrong field, a validate for a top-up), which takes the generic notice
// (D147). Either way a failed create keeps the card.
export const giftSpendRefusal = (
  code: string | undefined,
  product: GiftSpendProduct = 'class'
): string | null => (code && giftSpendRefusalCopy(product)[code]) || null;

export const giftSpendErrorText = (
  error: unknown,
  product: GiftSpendProduct = 'class'
): string =>
  giftSpendRefusal(getErrorCode(error), product) ??
  (isHttpStatus(error, 429)
    ? 'Too many tries. Wait a minute and try again.'
    : "We couldn't check this gift card. Please try again.");
